import React, { useState } from "react";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Building2, Save, X, MapPin, Phone, Mail, FileText, Clock, Loader2, Crosshair, Settings, Calendar, Layers } from 'lucide-react';
import SucursalForm from "./SucursalForm";
import HorariosLaboralesForm from "./HorariosLaboralesForm";

export default function ClienteFormDesktop({ cliente, cerrar, recargar }) {
  const { user } = useAuth();
  const [form, setForm] = useState(
    cliente
      ? {
          ...cliente,
          sucursales: Array.isArray(cliente.sucursales)
            ? cliente.sucursales
            : [],
          horarios_laborales: cliente.horarios_laborales || getHorariosDefecto(),
          horas_acumulables: cliente.horas_acumulables || false,
          inicio_acumulacion_mes: cliente.inicio_acumulacion_mes || new Date().getMonth() + 1,
          inicio_acumulacion_anio: cliente.inicio_acumulacion_anio || new Date().getFullYear(),
          tipo_frecuencia_mantenimiento: cliente.tipo_frecuencia_mantenimiento || "mensual",
          dia_mantenimiento_preferido: cliente.dia_mantenimiento_preferido || "",
          monitor_url: cliente.monitor_url || "",
          departamentos: Array.isArray(cliente.departamentos) ? cliente.departamentos : [],
          configuracion_ubicacion: cliente.configuracion_ubicacion || { usa_departamentos: false, departamento_obligatorio: false, label_departamento: 'Área/Departamento' },
        }
      : {
          nombre: "",
          ruc: "",
          telefono: "",
          email: "",
          direccion: "",
          ciudad: "",
          tipo: "ocasional",
          estado: "activo",
          horas_contratadas: 0,
          sucursales: [],
          observaciones: "",
          horarios_laborales: getHorariosDefecto(),
          horas_acumulables: false,
          inicio_acumulacion_mes: new Date().getMonth() + 1,
          inicio_acumulacion_anio: new Date().getFullYear(),
          tipo_frecuencia_mantenimiento: "mensual",
          dia_mantenimiento_preferido: "",
          monitor_url: "", // Inicializar vacío para nuevo cliente
          departamentos: [],
          configuracion_ubicacion: { usa_departamentos: false, departamento_obligatorio: false, label_departamento: 'Área/Departamento' },
        }
  );

  // Estados para validación en tiempo real
  const [errores, setErrores] = useState({
    ruc: "",
    email: ""
  });

  function getHorariosDefecto() {
    return {
      lunes: { activo: true, hora_inicio: '08:00', hora_fin: '18:00' },
      martes: { activo: true, hora_inicio: '08:00', hora_fin: '18:00' },
      miercoles: { activo: true, hora_inicio: '08:00', hora_fin: '18:00' },
      jueves: { activo: true, hora_inicio: '08:00', hora_fin: '18:00' },
      viernes: { activo: true, hora_inicio: '08:00', hora_fin: '18:00' },
      sabado: { activo: true, hora_inicio: '08:00', hora_fin: '12:00' },
      domingo: { activo: false, hora_inicio: '08:00', hora_fin: '12:00' }
    };
  }

  const [nuevaSucursal, setNuevaSucursal] = useState("");
  const [autoUbicando, setAutoUbicando] = useState(false);
  const [nuevoDepartamento, setNuevoDepartamento] = useState({ nombre: "", sucursal_nombre: "" });

  // Funciones de validación en tiempo real
  const validarRUC = (valor) => {
    if (!valor || valor.trim() === "") {
      return ""; // Vacío es válido (opcional)
    }
    
    const rucLimpio = valor.trim().replace(/\s+/g, '');
    
    // Validar formato: solo números y guiones
    if (!/^[\d-]+$/.test(rucLimpio)) {
      return "❌ El RUC solo debe contener números y guiones (sin espacios)";
    }
    
    // Verificar que no tenga múltiples guiones seguidos
    if (/--/.test(rucLimpio)) {
      return "❌ El RUC no puede tener guiones consecutivos";
    }
    
    return ""; // Válido
  };

  const validarEmail = (valor) => {
    if (!valor || valor.trim() === "") {
      return ""; // Vacío es válido (opcional)
    }
    
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (!emailRegex.test(valor.trim())) {
      return "❌ Email inválido (debe contener @ y un dominio válido)";
    }
    
    return ""; // Válido
  };

  // Handler para RUC con validación
  const handleRUCChange = (e) => {
    const valor = e.target.value;
    setForm({ ...form, ruc: valor });
    
    const error = validarRUC(valor);
    setErrores(prev => ({ ...prev, ruc: error }));
  };

  // Handler para Email con validación
  const handleEmailChange = (e) => {
    const valor = e.target.value;
    setForm({ ...form, email: valor });
    
    const error = validarEmail(valor);
    setErrores(prev => ({ ...prev, email: error }));
  };

  // Efecto para sincronizar el estado del formulario cuando cambia el prop cliente
  React.useEffect(() => {
    if (cliente) {
      setForm(prev => ({
        ...prev,
        ...cliente,
        sucursales: Array.isArray(cliente.sucursales) ? cliente.sucursales : [],
        horarios_laborales: cliente.horarios_laborales || getHorariosDefecto(),
        horas_acumulables: cliente.horas_acumulables || false,
        inicio_acumulacion_mes: cliente.inicio_acumulacion_mes || new Date().getMonth() + 1,
        inicio_acumulacion_anio: cliente.inicio_acumulacion_anio || new Date().getFullYear(),
        tipo_frecuencia_mantenimiento: cliente.tipo_frecuencia_mantenimiento || "mensual",
        dia_mantenimiento_preferido: cliente.dia_mantenimiento_preferido || "",
        monitor_url: cliente.monitor_url || "",
      }));
    }
  }, [cliente]);

  const diasSemana = [
    { key: 'lunes', nombre: 'Lunes', abrev: 'L' },
    { key: 'martes', nombre: 'Martes', abrev: 'M' },
    { key: 'miercoles', nombre: 'Miércoles', abrev: 'X' },
    { key: 'jueves', nombre: 'Jueves', abrev: 'J' },
    { key: 'viernes', nombre: 'Viernes', abrev: 'V' },
    { key: 'sabado', nombre: 'Sábado', abrev: 'S' },
    { key: 'domingo', nombre: 'Domingo', abrev: 'D' }
  ];

  const actualizarHorario = (dia, campo, valor) => {
    setForm(prev => ({
      ...prev,
      horarios_laborales: {
        ...prev.horarios_laborales,
        [dia]: {
          ...prev.horarios_laborales[dia],
          [campo]: valor
        }
      }
    }));
  };

  const toggleDiaHorario = (dia) => {
    setForm(prev => ({
      ...prev,
      horarios_laborales: {
        ...prev.horarios_laborales,
        [dia]: {
          ...prev.horarios_laborales[dia],
          activo: !prev.horarios_laborales[dia]?.activo
        }
      }
    }));
  };

  const aplicarHorarioATodos = (horarioBase) => {
    const nuevosHorarios = {};
    diasSemana.forEach(dia => {
      nuevosHorarios[dia.key] = {
        activo: form.horarios_laborales[dia.key]?.activo || false,
        hora_inicio: horarioBase.hora_inicio,
        hora_fin: horarioBase.hora_fin
      };
    });
    setForm(prev => ({
      ...prev,
      horarios_laborales: nuevosHorarios
    }));
  };

  const restaurarHorariosDefecto = () => {
    setForm(prev => ({
      ...prev,
      horarios_laborales: getHorariosDefecto()
    }));
  };

  const completarUbicacionAutomatica = () => {
    // Verificar disponibilidad de geolocalización
    if (!navigator.geolocation) {
      alert("La geolocalización no está disponible en este navegador. Puedes completar la ubicación manualmente.");
      return;
    }

    // Verificar protocolo y mostrar información de depuración
    console.log('🔍 Información de geolocalización:');
    console.log('- Protocolo:', location.protocol);
    console.log('- Hostname:', location.hostname);
    console.log('- URL completa:', location.href);
    console.log('- Navegador soporta geolocalización:', !!navigator.geolocation);
    
    if (location.protocol !== 'https:' && location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') {
      alert(`❌ Conexión no segura detectada
      
Protocolo actual: ${location.protocol}
URL: ${location.href}

La geolocalización requiere HTTPS. Por favor completa la ubicación manualmente.`);
      return;
    }

    setAutoUbicando(true);
    
    // Solicitar permisos explícitamente
    navigator.permissions?.query({name: 'geolocation'}).then((result) => {
      console.log('Estado de permisos de geolocalización:', result.state);
      
      if (result.state === 'denied') {
        const mensaje = `🔒 Permisos de ubicación bloqueados

Para habilitar la geolocalización automática:

🌐 Chrome/Edge:
1. Haz clic en el ícono de candado 🔒 junto a la URL
2. Selecciona "Ubicación" → "Permitir"
3. Recarga la página y vuelve a intentar

🦊 Firefox:
1. Haz clic en el ícono de escudo junto a la URL
2. Desactiva "Bloquear ubicación"
3. Recarga la página

📱 Móvil:
1. Ve a Configuración del navegador
2. Busca "Permisos de sitio" → "Ubicación"
3. Permite para ${window.location.hostname}

💡 Alternativa: Puedes completar la información manualmente.`;
        
        if (confirm(mensaje + '\n\n¿Quieres abrir la configuración de permisos del navegador?')) {
          // Intentar abrir configuración de permisos (solo funciona en algunos navegadores)
          try {
            if ('permissions' in navigator) {
              navigator.permissions.query({name: 'geolocation'});
            }
          } catch (e) {
            console.log('No se puede abrir configuración automáticamente');
          }
        }
        setAutoUbicando(false);
        return;
      }
      
      // Proceder con la geolocalización
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          try {
            const respuesta = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&addressdetails=1`,
              {
                headers: {
                  'Accept-Language': 'es',
                  'User-Agent': 'Elements Client Management System'
                },
              }
            );

            if (!respuesta.ok) {
              throw new Error('No se pudo obtener la dirección desde el servicio de mapas.');
            }

            const data = await respuesta.json();
            const address = data?.address || {};
            const ciudadPrincipal = address.city || address.town || address.village || address.county || '';
            const departamento = address.state || address.region || address.state_district || '';
            const pais = address.country || 'Paraguay';

            setForm((prev) => ({
              ...prev,
              direccion: prev.direccion || data?.display_name || '',
              ciudad: prev.ciudad || ciudadPrincipal || '',
              ubicacion_ciudad: ciudadPrincipal || prev.ubicacion_ciudad || '',
              ubicacion_departamento: departamento || prev.ubicacion_departamento || '',
              ubicacion_pais: pais || prev.ubicacion_pais || 'Paraguay',
              coordenadas_lat: latitude.toFixed(6),
              coordenadas_lng: longitude.toFixed(6),
            }));
            
            alert('¡Ubicación detectada y completada automáticamente!');
          } catch (error) {
            console.error('Error obteniendo información de ubicación:', error);
            alert('Se detectó tu posición pero no se pudo obtener la dirección. Puedes completar los datos manualmente.');
            
            // Al menos guardar las coordenadas
            setForm((prev) => ({
              ...prev,
              coordenadas_lat: latitude.toFixed(6),
              coordenadas_lng: longitude.toFixed(6),
            }));
          } finally {
            setAutoUbicando(false);
          }
        },
        (geoError) => {
          console.error('Error de geolocalización:', geoError);
          let mensaje = 'No se pudo acceder a tu ubicación. ';
          
          switch(geoError.code) {
            case geoError.PERMISSION_DENIED:
              mensaje += `Permisos denegados. 

🔧 Para habilitar:
• Chrome: Haz clic en el candado 🔒 → Ubicación → Permitir
• Firefox: Haz clic en el escudo → Desbloquear ubicación  
• Safari: Configuración → Privacidad → Ubicación → Permitir

Luego recarga la página e intenta nuevamente.`;
              break;
            case geoError.POSITION_UNAVAILABLE:
              mensaje += 'La ubicación no está disponible en este momento.';
              break;
            case geoError.TIMEOUT:
              mensaje += 'La solicitud de ubicación tardó demasiado tiempo.';
              break;
            default:
              mensaje += 'Error desconocido al obtener la ubicación.';
          }
          
          alert(mensaje);
          setAutoUbicando(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 300000 // 5 minutos de cache
        }
      );
    }).catch((error) => {
      console.error('Error verificando permisos:', error);
      // Fallback: intentar directamente si no hay API de permisos
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const respuesta = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&addressdetails=1`,
              {
                headers: {
                  'Accept-Language': 'es',
                  'User-Agent': 'Elements Client Management System'
                },
              }
            );

            if (!respuesta.ok) throw new Error('Servicio de mapas no disponible');

            const data = await respuesta.json();
            const address = data?.address || {};
            const ciudadPrincipal = address.city || address.town || address.village || address.county || '';
            const departamento = address.state || address.region || address.state_district || '';
            const pais = address.country || 'Paraguay';

            setForm((prev) => ({
              ...prev,
              direccion: data?.display_name || prev.direccion || '',
              ciudad: ciudadPrincipal || prev.ciudad || '',
              ubicacion_ciudad: ciudadPrincipal || prev.ubicacion_ciudad || '',
              ubicacion_departamento: departamento || prev.ubicacion_departamento || '',
              ubicacion_pais: pais || prev.ubicacion_pais || 'Paraguay',
              coordenadas_lat: latitude.toFixed(6),
              coordenadas_lng: longitude.toFixed(6),
            }));
            
            alert('¡Ubicación completada automáticamente!');
          } catch (error) {
            console.error('Error en servicio de mapas:', error);
            setForm((prev) => ({
              ...prev,
              coordenadas_lat: latitude.toFixed(6),
              coordenadas_lng: longitude.toFixed(6),
            }));
            alert('Coordenadas guardadas. Completa manualmente ciudad y departamento.');
          } finally {
            setAutoUbicando(false);
          }
        },
        (geoError) => {
          console.error('Geolocalización falló:', geoError);
          alert('No se pudo detectar tu ubicación. Completa la información manualmente.');
          setAutoUbicando(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 300000
        }
      );
    });
  };

  const completarDesdeGoogleMaps = async () => {
    let url = prompt('📍 Pega el enlace de Google Maps aquí:\n\nPuedes usar:\n• Enlaces completos: https://www.google.com/maps/...\n• Enlaces cortos: https://maps.app.goo.gl/...\n• O simplemente las coordenadas: -25.123, -57.456');
    
    if (!url || !url.trim()) return;
    
    url = url.trim();
    setAutoUbicando(true);
    
    try {
      let lat = null, lng = null;
      
      // Primero intentar si pegó coordenadas directamente
      const coordPattern = /^(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)$/;
      const coordMatch = url.match(coordPattern);
      
      if (coordMatch) {
        lat = parseFloat(coordMatch[1]);
        lng = parseFloat(coordMatch[2]);
        console.log('Coordenadas directas detectadas:', lat, lng);
      }
      
      // Si no son coordenadas directas, intentar extraer de URL
      if (!lat || !lng) {
        // Para enlaces acortados, pedir al usuario que abra el enlace
        if (url.includes('goo.gl') || url.includes('maps.app.goo.gl')) {
          const instrucciones = `🔗 Enlace acortado detectado

Por favor sigue estos pasos:

1️⃣ Abre este enlace en una nueva pestaña de tu navegador
2️⃣ Espera a que se cargue Google Maps
3️⃣ Copia la URL completa de la barra de direcciones
4️⃣ Pégala aquí

O más fácil:
1️⃣ Haz clic derecho en el lugar en Google Maps
2️⃣ Copia las coordenadas (ej: -25.263, -57.606)
3️⃣ Pégalas aquí cuando te lo pida

¿Quieres abrir el enlace ahora?`;
          
          if (confirm(instrucciones)) {
            window.open(url, '_blank');
            setAutoUbicando(false);
            
            // Dar tiempo y volver a pedir
            setTimeout(() => {
              completarDesdeGoogleMaps();
            }, 1000);
            return;
          } else {
            setAutoUbicando(false);
            return;
          }
        }
        
        // Intentar extraer coordenadas de URL completa
        // Patrón 1: @-25.1234567,-57.1234567
        const pattern1 = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
        const match1 = url.match(pattern1);
        
        if (match1) {
          lat = parseFloat(match1[1]);
          lng = parseFloat(match1[2]);
        } else {
          // Patrón 2: /place/.../@-25.1234567,-57.1234567
          const pattern2 = /place\/[^/]+\/@(-?\d+\.\d+),(-?\d+\.\d+)/;
          const match2 = url.match(pattern2);
          
          if (match2) {
            lat = parseFloat(match2[1]);
            lng = parseFloat(match2[2]);
          } else {
            // Patrón 3: ?q=-25.1234567,-57.1234567
            const pattern3 = /[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/;
            const match3 = url.match(pattern3);
            
            if (match3) {
              lat = parseFloat(match3[1]);
              lng = parseFloat(match3[2]);
            } else {
              // Patrón 4: ll=-25.1234567,-57.1234567
              const pattern4 = /[?&]ll=(-?\d+\.\d+),(-?\d+\.\d+)/;
              const match4 = url.match(pattern4);
              
              if (match4) {
                lat = parseFloat(match4[1]);
                lng = parseFloat(match4[2]);
              } else {
                // Patrón 5: !3d-25.1234567!4d-57.1234567 (formato interno de Google)
                const pattern5 = /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/;
                const match5 = url.match(pattern5);
                
                if (match5) {
                  lat = parseFloat(match5[1]);
                  lng = parseFloat(match5[2]);
                }
              }
            }
          }
        }
      }
      
      if (!lat || !lng) {
        alert(`❌ No se pudieron extraer coordenadas

URL recibida: ${url}

💡 Prueba estas opciones:

1️⃣ OPCIÓN MÁS FÁCIL - Copiar coordenadas directamente:
   • Abre Google Maps
   • Haz clic derecho en el lugar
   • Copia las coordenadas (ejemplo: -25.263, -57.606)
   • Pégalas aquí

2️⃣ Usar URL completa (no acortada):
   • En Google Maps, comparte la ubicación
   • Elige "Copiar enlace"
   • Asegúrate que empiece con https://www.google.com/maps/...
   
3️⃣ Usa el botón "Usar GPS" si estás en el lugar`);
        setAutoUbicando(false);
        return;
      }
      
      console.log('✅ Coordenadas extraídas:', lat, lng);
      
      // Usar geocodificación inversa con las coordenadas extraídas
      const respuesta = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1`,
        {
          headers: {
            'Accept-Language': 'es',
            'User-Agent': 'Elements Client Management System'
          },
        }
      );

      if (!respuesta.ok) {
        throw new Error('No se pudo obtener la dirección desde el servicio de mapas.');
      }

      const data = await respuesta.json();
      const address = data?.address || {};
      
      // Construir dirección más específica
      const partes = [];
      if (address.road) partes.push(address.road);
      if (address.house_number) partes.push(address.house_number);
      if (address.neighbourhood) partes.push(address.neighbourhood);
      
      const direccionEspecifica = partes.length > 0 ? partes.join(', ') : data?.display_name || '';
      const ciudadPrincipal = address.city || address.town || address.village || address.county || '';
      const departamento = address.state || address.region || address.state_district || '';
      const pais = address.country || 'Paraguay';

      setForm((prev) => ({
        ...prev,
        direccion: direccionEspecifica,
        ciudad: ciudadPrincipal,
        ubicacion_ciudad: ciudadPrincipal,
        ubicacion_departamento: departamento,
        ubicacion_pais: pais,
        coordenadas_lat: lat.toFixed(6),
        coordenadas_lng: lng.toFixed(6),
      }));
      
      alert(`✅ ¡Ubicación completada!

📍 Coordenadas: ${lat.toFixed(6)}, ${lng.toFixed(6)}
🏙️ Ciudad: ${ciudadPrincipal}
📌 Dirección: ${direccionEspecifica}`);
    } catch (error) {
      console.error('Error procesando ubicación:', error);
      alert('❌ Error al procesar la ubicación.\n\nIntenta copiar las coordenadas directamente desde Google Maps.');
    } finally {
      setAutoUbicando(false);
    }
  };

  async function guardar() {
    try {
      // Validar campos requeridos
      if (!form.nombre?.trim()) {
        alert("El nombre del cliente es requerido");
        return;
      }
      if (!form.tipo?.trim()) {
        alert("El tipo de cliente es requerido");
        return;
      }

      // Validar RUC si tiene valor
      if (form.ruc && form.ruc.trim()) {
        const errorRuc = validarRUC(form.ruc);
        if (errorRuc) {
          alert("Error en RUC: " + errorRuc.replace("❌ ", ""));
          return;
        }
      }

      // Validar email si tiene valor
      if (form.email && form.email.trim()) {
        const errorEmail = validarEmail(form.email);
        if (errorEmail) {
          alert("Error en Email: " + errorEmail.replace("❌ ", ""));
          return;
        }
      }

      // Convertir coordenadas a números o null
      const coordenadas_lat = form.coordenadas_lat ? parseFloat(form.coordenadas_lat) : null;
      const coordenadas_lng = form.coordenadas_lng ? parseFloat(form.coordenadas_lng) : null;
      
      // Procesar sucursales: convertir coordenadas a números
      const sucursalesProcesadas = Array.isArray(form.sucursales) 
        ? form.sucursales.map(suc => ({
            ...suc,
            coordenadas_lat: suc.coordenadas_lat ? parseFloat(suc.coordenadas_lat) : null,
            coordenadas_lng: suc.coordenadas_lng ? parseFloat(suc.coordenadas_lng) : null
          }))
        : [];

      // Asegurarse de que las sucursales sean una lista
      const dataToSend = {
        ...form,
        coordenadas_lat,
        coordenadas_lng,
        sucursales: sucursalesProcesadas,
        departamentos: Array.isArray(form.departamentos) ? form.departamentos : [],
        configuracion_ubicacion: form.configuracion_ubicacion || null,
        horas_contratadas: form.horas_contratadas || 0,
        // Sanitize new fields to ensure they match backend schema (int or null, not empty string)
        tipo_frecuencia_mantenimiento: form.tipo_frecuencia_mantenimiento || null,
        dia_mantenimiento_preferido: form.dia_mantenimiento_preferido ? parseInt(form.dia_mantenimiento_preferido) : null,
        inicio_acumulacion_mes: form.inicio_acumulacion_mes ? parseInt(form.inicio_acumulacion_mes) : null,
        inicio_acumulacion_anio: form.inicio_acumulacion_anio ? parseInt(form.inicio_acumulacion_anio) : null,
      };

      if (cliente) {
        const id = cliente?._id || cliente?.id;
        if (!id) {
          throw new Error("ID de cliente no disponible para actualizar");
        }
        const response = await http.put(`/clientes/${id}`, dataToSend);
        if (!response.data) {
          throw new Error("No se recibió respuesta del servidor");
        }
      } else {
        const response = await http.post("/clientes", dataToSend);
        if (!response.data) {
          throw new Error("No se recibió respuesta del servidor");
        }
      }
      recargar();
      cerrar();
    } catch (err) {
      console.error("Error guardando cliente", err);
      const mensaje = err.response?.data?.detail || err.message || "Error al guardar cliente";
      alert(mensaje);
    }
  }

  function agregarSucursal() {
    if (!nuevaSucursal.trim()) return;
    // Crear objeto de sucursal básico
    const nuevaSucursalObj = {
      nombre: nuevaSucursal.trim(),
      ubicacion_ciudad: "",
      ubicacion_direccion: "",
      ubicacion_remota: false,
      distancia_km: 0,
      coordenadas_lat: null,
      coordenadas_lng: null,
      orden_prioridad: 0,
      observaciones: ""
    };
    setForm({ ...form, sucursales: [...form.sucursales, nuevaSucursalObj] });
    setNuevaSucursal("");
  }

  function eliminarSucursal(i) {
    const nuevas = form.sucursales.filter((_, idx) => idx !== i);
    setForm({ ...form, sucursales: nuevas });
  }

  function actualizarSucursal(sucursalData, index) {
    const nuevasSucursales = [...form.sucursales];
    nuevasSucursales[index] = sucursalData;
    setForm({ ...form, sucursales: nuevasSucursales });
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/45 backdrop-blur-sm animate-modal-overlay">
      <div className="relative mx-4 w-full max-w-4xl">
        <div className="flex max-h-[95vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 animate-modal-container">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-emerald-200 bg-white text-emerald-600 shadow-sm">
                <Building2 className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900">
                  {cliente ? "Editar Cliente" : "Nuevo Cliente"}
                </h3>
                <p className="text-sm text-slate-600">
                  {cliente ? "Modifica la información del cliente" : "Ingresa los datos del nuevo cliente"}
                </p>
              </div>
            </div>
            <button
              onClick={cerrar}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-transparent text-slate-400 transition-colors hover:border-slate-200 hover:text-slate-600"
              aria-label="Cerrar formulario"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 pb-6 pt-5 space-y-6">
          {/* Información General */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Información General
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Cliente *
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Ingresa el nombre del cliente"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Cliente *
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                >
                  <option value="ocasional">Ocasional</option>
                  <option value="mensual">Mensual</option>
                  <option value="empresa">Empresa</option>
                  <option value="gobierno">Gobierno</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  RUC (Registro Único del Contribuyente)
                </label>
                <input
                  type="text"
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${
                    errores.ruc 
                      ? 'border-red-400 focus:ring-red-500 focus:border-red-500 bg-red-50' 
                      : 'border-gray-300 focus:ring-emerald-500 focus:border-emerald-500'
                  }`}
                  placeholder="Ej: 80012345-6 (sin espacios)"
                  value={form.ruc || ""}
                  onChange={handleRUCChange}
                />
                {errores.ruc ? (
                  <p className="text-xs text-red-600 mt-1 font-medium">
                    {errores.ruc}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    ✓ Solo números y guiones, sin espacios (Ej: 80012345-6)
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  value={form.estado}
                  onChange={(e) => setForm({ ...form, estado: e.target.value })}
                >
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                  <option value="suspendido">Suspendido</option>
                  <option value="prospecto">Prospecto</option>
                </select>
              </div>

              {(form.tipo === "mensual" || form.tipo === "empresa") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Horas Contratadas (mensual)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Ej: 40"
                    value={form.horas_contratadas}
                    onChange={(e) => setForm({ ...form, horas_contratadas: e.target.value })}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Información de Contacto */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5 text-green-600" />
              Información de Contacto
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  Email
                </label>
                <input
                  type="email"
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${
                    errores.email 
                      ? 'border-red-400 focus:ring-red-500 focus:border-red-500 bg-red-50' 
                      : 'border-gray-300 focus:ring-emerald-500 focus:border-emerald-500'
                  }`}
                  placeholder="correo@empresa.com"
                  value={form.email}
                  onChange={handleEmailChange}
                />
                {errores.email ? (
                  <p className="text-xs text-red-600 mt-1 font-medium">
                    {errores.email}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    ✓ Debe contener @ y un dominio válido
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  Teléfono
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Ej: +595 21 123456 o 0981 123456"
                  value={form.telefono}
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  Dirección
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Calle, carrera, número"
                  value={form.direccion}
                  onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ciudad (antigua)
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Ciudad donde se encuentra"
                  value={form.ciudad || ""}
                  onChange={(e) => setForm({ ...form, ciudad: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Ubicación Geográfica */}
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-5 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <h4 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <MapPin className="h-5 w-5 text-emerald-600" />
                Ubicación Geográfica
              </h4>
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={completarUbicacionAutomatica}
                  disabled={autoUbicando}
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {autoUbicando ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Crosshair className="h-4 w-4" />
                  )}
                  {autoUbicando ? "Detectando..." : "Usar GPS"}
                </button>
                <button
                  type="button"
                  onClick={completarDesdeGoogleMaps}
                  disabled={autoUbicando}
                  className="inline-flex items-center gap-2 rounded-full bg-green-600 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <MapPin className="h-4 w-4" />
                  Desde Google Maps
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const ayuda = `🗺️ AYUDA PARA HABILITAR UBICACIÓN

🌐 Google Chrome / Microsoft Edge:
1. Busca el ícono de candado 🔒 a la izquierda de la URL
2. Haz clic en él
3. Selecciona "Ubicación"
4. Cambia a "Permitir"
5. Recarga la página (F5)

🦊 Mozilla Firefox:
1. Busca el ícono de escudo 🛡️ junto a la URL
2. Haz clic en él
3. Desactiva "Bloquear acceso a ubicación"
4. Recarga la página (F5)

🍎 Safari:
1. Ve a Safari → Configuración → Sitios web
2. Selecciona "Ubicación" en la barra lateral
3. Encuentra ${window.location.hostname}
4. Cambia a "Permitir"

📱 Navegadores móviles:
1. Toca el ícono de información (ⓘ) en la URL
2. Busca "Permisos" o "Ubicación"
3. Cambia a "Permitir"
4. Recarga la página

✨ Una vez habilitado, el botón azul detectará automáticamente tu ciudad y coordenadas.`;
                    alert(ayuda);
                  }}
                  className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-emerald-700 transition-all hover:bg-emerald-50"
                >
                  ❓ ¿Cómo habilitar?
                </button>
              </div>
            </div>
            <p className="mt-2 text-sm text-emerald-700">
              🗺️ Esta información se usa para detectar automáticamente sucursales remotas
            </p>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ciudad Principal *
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Ej: Asunción"
                  value={form.ubicacion_ciudad || ""}
                  onChange={(e) => setForm({ ...form, ubicacion_ciudad: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Departamento/Estado
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Ej: Central"
                  value={form.ubicacion_departamento || ""}
                  onChange={(e) => setForm({ ...form, ubicacion_departamento: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  País
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  value={form.ubicacion_pais || "Paraguay"}
                  onChange={(e) => setForm({ ...form, ubicacion_pais: e.target.value })}
                >
                  <option value="Paraguay">Paraguay</option>
                  <option value="Argentina">Argentina</option>
                  <option value="Brasil">Brasil</option>
                  <option value="Uruguay">Uruguay</option>
                  <option value="Chile">Chile</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Latitud (opcional)
                </label>
                <input
                  type="number"
                  step="0.000001"
                  min="-90"
                  max="90"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Ej: -25.2637"
                  value={form.coordenadas_lat || ""}
                  onChange={(e) => setForm({ ...form, coordenadas_lat: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Para futuras funcionalidades de mapas</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Longitud (opcional)
                </label>
                <input
                  type="number"
                  step="0.000001"
                  min="-180"
                  max="180"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Ej: -57.5759"
                  value={form.coordenadas_lng || ""}
                  onChange={(e) => setForm({ ...form, coordenadas_lng: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Coordenadas GPS de la sede principal</p>
              </div>
            </div>
          </div>

          {/* Observaciones */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observaciones
            </label>
            <textarea
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Notas adicionales sobre el cliente..."
              value={form.observaciones || ""}
              onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
            />
          </div>

          {/* Sucursales */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              Sucursales
            </h4>
            <div className="border rounded-lg p-4 bg-gray-50">
              <SucursalForm
                sucursales={form.sucursales}
                nuevaSucursal={nuevaSucursal}
                setNuevaSucursal={setNuevaSucursal}
                agregarSucursal={agregarSucursal}
                eliminarSucursal={eliminarSucursal}
                actualizarSucursal={actualizarSucursal}
                clienteId={cliente?._id || cliente?.id}
              />
            </div>
          </div>

          {/* Departamentos / Áreas */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Layers className="w-5 h-5 text-indigo-600" />
              {form.configuracion_ubicacion?.label_departamento || 'Áreas / Departamentos'}
            </h4>
            <div className="border rounded-lg p-4 bg-indigo-50">
              {/* Toggle: usa departamentos */}
              <div className="flex items-center gap-3 mb-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.configuracion_ubicacion?.usa_departamentos || false}
                    onChange={(e) => setForm({
                      ...form,
                      configuracion_ubicacion: {
                        ...form.configuracion_ubicacion,
                        usa_departamentos: e.target.checked
                      }
                    })}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Usa departamentos/áreas
                </label>
                {form.configuracion_ubicacion?.usa_departamentos && (
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.configuracion_ubicacion?.departamento_obligatorio || false}
                      onChange={(e) => setForm({
                        ...form,
                        configuracion_ubicacion: {
                          ...form.configuracion_ubicacion,
                          departamento_obligatorio: e.target.checked
                        }
                      })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Obligatorio
                  </label>
                )}
              </div>

              {form.configuracion_ubicacion?.usa_departamentos && (
                <>
                  {/* Etiqueta personalizada */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Etiqueta personalizada</label>
                    <input
                      type="text"
                      className="w-48 border border-gray-300 rounded px-2 py-1 text-sm"
                      placeholder="Ej: Área, Departamento, Sector"
                      value={form.configuracion_ubicacion?.label_departamento || ''}
                      onChange={(e) => setForm({
                        ...form,
                        configuracion_ubicacion: {
                          ...form.configuracion_ubicacion,
                          label_departamento: e.target.value
                        }
                      })}
                    />
                  </div>

                  {/* Lista de departamentos */}
                  {(form.departamentos || []).map((dep, idx) => (
                    <div key={idx} className="flex items-center gap-2 mb-2 bg-white rounded p-2 border">
                      <span className="text-sm font-medium flex-1">{dep.nombre}</span>
                      {dep.sucursal_nombre && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                          {dep.sucursal_nombre}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const depsCopy = [...form.departamentos];
                          depsCopy.splice(idx, 1);
                          setForm({ ...form, departamentos: depsCopy });
                        }}
                        className="text-red-500 hover:text-red-700 text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  ))}

                  {/* Agregar departamento */}
                  <div className="flex gap-2 mt-3">
                    <input
                      type="text"
                      className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
                      placeholder="Nombre del departamento"
                      value={nuevoDepartamento.nombre}
                      onChange={(e) => setNuevoDepartamento({ ...nuevoDepartamento, nombre: e.target.value })}
                    />
                    {form.sucursales?.length > 0 && (
                      <select
                        className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                        value={nuevoDepartamento.sucursal_nombre}
                        onChange={(e) => setNuevoDepartamento({ ...nuevoDepartamento, sucursal_nombre: e.target.value })}
                      >
                        <option value="">Sin sucursal</option>
                        {form.sucursales.map((suc, i) => (
                          <option key={i} value={typeof suc === 'string' ? suc : suc.nombre}>
                            {typeof suc === 'string' ? suc : suc.nombre}
                          </option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        if (!nuevoDepartamento.nombre.trim()) return;
                        setForm({
                          ...form,
                          departamentos: [
                            ...(form.departamentos || []),
                            { nombre: nuevoDepartamento.nombre.trim(), sucursal_nombre: nuevoDepartamento.sucursal_nombre || null, activo: true }
                          ]
                        });
                        setNuevoDepartamento({ nombre: "", sucursal_nombre: "" });
                      }}
                      className="bg-indigo-600 text-white px-3 py-1.5 rounded text-sm hover:bg-indigo-700"
                    >
                      Agregar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Horarios Laborales */}
          <HorariosLaboralesForm
            horarios={form.horarios_laborales}
            onUpdate={actualizarHorario}
            onToggleDia={toggleDiaHorario}
            onAplicarATodos={aplicarHorarioATodos}
            onRestaurar={restaurarHorariosDefecto}
          />

          {/* Configuración Avanzada (Solo Admin) */}
          {user?.rol === 'admin' && (
            <div className="mt-8 border-t pt-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-gray-600" />
                Configuración Avanzada
              </h4>
              
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 space-y-6">
                {/* Horas Acumulables */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <input
                      type="checkbox"
                      id="horas_acumulables"
                      className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500 border-gray-300"
                      checked={form.horas_acumulables || false}
                      onChange={(e) => setForm({ ...form, horas_acumulables: e.target.checked })}
                    />
                    <label htmlFor="horas_acumulables" className="font-medium text-gray-900 cursor-pointer select-none">
                      Habilitar Horas Acumulables
                    </label>
                  </div>
                  
                  {form.horas_acumulables && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-8 animate-in fade-in slide-in-from-top-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Mes de Inicio
                        </label>
                        <select
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          value={form.inicio_acumulacion_mes || new Date().getMonth() + 1}
                          onChange={(e) => setForm({ ...form, inicio_acumulacion_mes: parseInt(e.target.value) })}
                        >
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {new Date(0, i).toLocaleString('es-ES', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Año de Inicio
                        </label>
                        <select
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          value={form.inicio_acumulacion_anio || new Date().getFullYear()}
                          onChange={(e) => setForm({ ...form, inicio_acumulacion_anio: parseInt(e.target.value) })}
                        >
                          {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i).map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                      <p className="col-span-full text-sm text-gray-500 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Las horas no utilizadas se acumularán a partir de esta fecha.
                      </p>
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-200 my-4"></div>

                {/* Día de Mantenimiento */}
                <div>
                  <label className="block font-medium text-gray-900 mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-600" />
                    Preferencia de Mantenimiento
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Frecuencia</label>
                      <select
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={form.tipo_frecuencia_mantenimiento || "mensual"}
                        onChange={(e) => setForm({ ...form, tipo_frecuencia_mantenimiento: e.target.value, dia_mantenimiento_preferido: null })}
                      >
                        <option value="mensual">Mensual (Día del mes)</option>
                        <option value="semanal">Semanal (Día de la semana)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        {form.tipo_frecuencia_mantenimiento === 'semanal' ? 'Día de la semana' : 'Día del mes'}
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={form.dia_mantenimiento_preferido || ""}
                        onChange={(e) => setForm({ ...form, dia_mantenimiento_preferido: e.target.value ? parseInt(e.target.value) : "" })}
                      >
                        <option value="">Sin preferencia</option>
                        {form.tipo_frecuencia_mantenimiento === 'semanal' ? (
                          <>
                            <option value="1">Lunes</option>
                            <option value="2">Martes</option>
                            <option value="3">Miércoles</option>
                            <option value="4">Jueves</option>
                            <option value="5">Viernes</option>
                            <option value="6">Sábado</option>
                            <option value="7">Domingo</option>
                          </>
                        ) : (
                          Array.from({ length: 31 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              Día {i + 1}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    El simulador de rutas priorizará este día para las visitas.
                  </p>
                </div>
              </div>

               {/* UPTIME KUMA INTEGRATION */}
              <div className="grid grid-cols-1 gap-4 pt-4 border-t border-slate-200 mt-4">
                <h4 className="text-sm font-medium text-slate-900 flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Monitoreo de Servicios (Uptime Kuma)
                </h4>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Enlace Página de Estado
                  </label>
                  <div className="flex rounded-lg shadow-sm">
                    <span className="inline-flex items-center px-4 rounded-l-lg border border-r-0 border-slate-200 bg-slate-50 text-slate-500 text-sm font-medium">
                      https://
                    </span>
                    <input
                      type="text"
                      className="flex-1 min-w-0 block w-full px-3 py-2 border border-slate-200 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent sm:text-sm"
                      placeholder="kuma.elements.com.py/status/cliente"
                      value={form.monitor_url ? form.monitor_url.replace(/^https?:\/\//, '') : ''}
                      onChange={(e) => setForm({...form, monitor_url: e.target.value})}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
                    <Settings className="w-3 h-3" />
                    Pega aquí el enlace de la Status Page pública creada en Uptime Kuma para este cliente.
                  </p>
                </div>
              </div>

            </div>
          )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-4">
            <button
              onClick={cerrar}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-5 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-100"
            >
              <X className="h-4 w-4" />
              Cancelar
            </button>
            <button
              onClick={guardar}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
            >
              <Save className="h-4 w-4" />
              {cliente ? "Actualizar" : "Crear"} Cliente
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
